import * as React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import {
  Tooltip,
  Menu,
  MenuItem,
  IconButton as MuiIconButton,
  Avatar,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from '@mui/material';

import { Moon, Sun, User, LogOut, HelpCircle } from 'lucide-react';

import { signOut } from 'thunk';
import { selectAuthUser } from 'AuthModule/selectors';
import { setTheme } from 'actionCreators';
import { THEMES } from '@constants';
import type { AppStateType } from 'reducers';
import { StyledProps } from 'types/styled';

const IconButton = styled(MuiIconButton)(({ theme }) => ({
  padding: theme.spacing(1), // 4px
}));

const UserAvatar = styled(Avatar)<StyledProps>(({ theme }) => ({
  width: 32,
  height: 32,
  fontSize: '0.875rem',
  fontWeight: 600,
  backgroundColor: theme.palette.action.selected,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.15s ease',
  '&:hover': {
    borderColor: theme.palette.action.disabled,
  },
}));

function UserDropdown() {
  const [anchorMenu, setAnchorMenu] = React.useState<any>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const authUser = useAppSelector(selectAuthUser);
  const currentTheme = useAppSelector((state: AppStateType) => state.themeReducer.currentTheme);

  const toggleMenu = (event: React.SyntheticEvent) => {
    setAnchorMenu(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorMenu(null);
  };

  const handleSignOut = async () => {
    closeMenu();
    dispatch(signOut());
  };

  const handleThemeToggle = () => {
    const newTheme = currentTheme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
    dispatch(setTheme(newTheme));
    closeMenu();
  };

  const handleProfileClick = () => {
    closeMenu();
    // TODO: Navigate to user profile/settings page when implemented
    // navigate('/profile') or navigate('/settings')
  };

  const initials =
    authUser?.account?.initials || authUser?.account?.name?.substring(0, 2).toUpperCase() || 'U';
  const userName = authUser?.account?.name || 'User';
  const isDarkMode = currentTheme === THEMES.DARK;

  return (
    <React.Fragment>
      <Tooltip title={userName}>
        <IconButton
          aria-owns={anchorMenu ? 'menu-appbar' : undefined}
          aria-haspopup="true"
          onClick={toggleMenu}
          color="inherit"
        >
          <UserAvatar>{initials}</UserAvatar>
        </IconButton>
      </Tooltip>
      <Menu
        id="menu-appbar"
        anchorEl={anchorMenu}
        open={Boolean(anchorMenu)}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              minWidth: 200,
              borderRadius: (theme) => theme.spacing(0.5), // 2px
            },
          },
        }}
      >
        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <User size={18} />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        {/* GLX-1800: link to the Galaxy MCP usage guide */}
        <MenuItem
          component="a"
          href="https://app.notion.com/p/392f8556468c81c892a0c895aeed695f"
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeMenu}
        >
          <ListItemIcon>
            <HelpCircle size={18} />
          </ListItemIcon>
          <ListItemText>Galaxy MCP guide</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        <MenuItem onClick={handleThemeToggle}>
          <ListItemIcon>{isDarkMode ? <Sun size={18} /> : <Moon size={18} />}</ListItemIcon>
          <ListItemText>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <LogOut size={18} />
          </ListItemIcon>
          <ListItemText>Sign out</ListItemText>
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
}

export default UserDropdown;
